import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

const SCANNER_ID = "shared-barcode-scanner-region";

// Common retail 1D barcodes + QR.
const FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.QR_CODE,
];

// Camera barcode/SKU scanner in a dialog. Calls onDetected(code) once a code is
// read, then closes. Requires HTTPS + camera permission (both true on prod).
const BarcodeScanner = ({ open, onClose, onDetected, title = "Scan barcode" }) => {
  const scannerRef = useRef(null);
  const handledRef = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    handledRef.current = false;
    setError("");
    let cancelled = false;

    const stop = async () => {
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        try {
          await s.stop();
        } catch (e) {
          /* already stopped */
        }
        try {
          s.clear();
        } catch (e) {
          /* noop */
        }
      }
    };

    const start = async () => {
      if (!document.getElementById(SCANNER_ID)) return;
      try {
        const html5 = new Html5Qrcode(SCANNER_ID, {
          formatsToSupport: FORMATS,
          verbose: false,
        });
        scannerRef.current = html5;
        await html5.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (decodedText) => {
            if (cancelled || handledRef.current) return;
            handledRef.current = true;
            onDetected(decodedText);
            onClose();
          },
          () => {} // ignore per-frame decode misses
        );
      } catch (e) {
        if (!cancelled) {
          setError(
            "Unable to access the camera. Allow camera permission in your browser, and note the camera only works over HTTPS."
          );
        }
      }
    };

    const t = setTimeout(start, 250); // let the dialog mount its region
    return () => {
      cancelled = true;
      clearTimeout(t);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Box
              id={SCANNER_ID}
              sx={{
                width: "100%",
                minHeight: 240,
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "background.secondary",
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, textAlign: "center" }}
            >
              Point the rear camera at the product barcode.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BarcodeScanner;
